import { Flame, KeyRound, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLang } from "@/lib/i18n";

export default function RevealPanel({
  meta,
  status,
  password,
  setPassword,
  confirmedBurn,
  setConfirmedBurn,
  error,
  revealing,
  onReveal,
}) {
  const { t } = useLang();
  const passwordMissing = meta?.has_password && !password;
  const burnMissing = meta?.one_time && !confirmedBurn;
  const disabled = revealing || passwordMissing || burnMissing;

  return (
    <div className="border border-black/15 dark:border-white/15 p-6 md:p-10 space-y-6" data-testid="reveal-panel">
      {meta?.one_time && (
        <div className="border border-[#ff3b30]/40 bg-[#ff3b30]/5 p-4 flex items-start gap-3">
          <Flame className="w-4 h-4 text-[#ff3b30] shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-neutral-800 dark:text-neutral-200 leading-relaxed">{t("viewOneTime")}</p>
        </div>
      )}
      {!meta?.one_time && (
        <div className="border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 p-4 text-xs text-neutral-600 dark:text-neutral-400 font-mono">
          {t("viewMulti")}
        </div>
      )}

      {status === "needs-password" && (
        <div>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-500 mb-3">
            <KeyRound className="w-3.5 h-3.5" /> {t("passwordRequired")}
          </div>
          <Input
            data-testid="unlock-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("enterPassword")}
            className="bg-transparent border border-black/10 dark:border-white/10 rounded-none font-mono"
            onKeyDown={(e) => e.key === "Enter" && onReveal()}
          />
        </div>
      )}

      {meta?.one_time && (
        <label
          className="flex items-start gap-3 text-xs text-neutral-700 dark:text-neutral-300 cursor-pointer"
          data-testid="confirm-burn-label"
        >
          <input
            type="checkbox"
            checked={confirmedBurn}
            onChange={(e) => setConfirmedBurn(e.target.checked)}
            data-testid="confirm-burn-checkbox"
            className="mt-0.5 accent-[#ff3b30]"
          />
          <span>{t("revealConfirm")}</span>
        </label>
      )}

      {error && (
        <p
          className="text-[#ff3b30] text-xs font-mono uppercase tracking-widest"
          data-testid="reveal-error"
        >
          {error}
        </p>
      )}

      <Button
        data-testid="reveal-button"
        onClick={onReveal}
        disabled={disabled}
        className="w-full h-12 rounded-none bg-black dark:bg-white text-white dark:text-black hover:bg-[#00ff66] font-bold uppercase tracking-widest text-xs disabled:opacity-40"
      >
        {revealing ? (
          t("revealing")
        ) : (
          <span className="inline-flex items-center gap-2">
            <Eye className="w-4 h-4" /> {t("reveal")}
          </span>
        )}
      </Button>
    </div>
  );
}
